// pages/Dashboard/Appointments.js
import { useState, useMemo } from 'react';
import {
  FaCalendarAlt,
  FaClock,
  FaUserPlus,
  FaChevronLeft,
  FaChevronRight,
  FaUserCircle,
  FaMobileAlt,
  FaCut,
  FaCheckCircle,
  FaHourglassHalf,
  FaWalking,
  FaTimes,
  FaListUl,
  FaThLarge,
} from 'react-icons/fa';
import '../Css/Appointments.css';

const STAFF_LIST = ['Riya Sharma', 'Ankit Kapoor', 'Meena Iyer', 'Sohail Khan'];
const SERVICES = ['Haircut', 'Hair Color', 'Facial', 'Manicure', 'Pedicure', 'Hair Spa'];
const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00',
];

const STATUS_STYLES = {
  upcoming: { label: 'Upcoming', icon: <FaHourglassHalf />, cls: 'status-upcoming' },
  progress: { label: 'In Progress', icon: <FaClock />, cls: 'status-progress' },
  completed: { label: 'Completed', icon: <FaCheckCircle />, cls: 'status-completed' },
  walkin: { label: 'Walk-in', icon: <FaWalking />, cls: 'status-walkin' },
};

const initialAppointments = [
  { id: 1, client: 'Priya Mehta', phone: '98765 43210', service: 'Haircut', staff: 'Riya Sharma', time: '10:00', status: 'completed' },
  { id: 2, client: 'Ankita Rao', phone: '98123 45678', service: 'Hair Color', staff: 'Ankit Kapoor', time: '11:00', status: 'progress' },
  { id: 3, client: 'Sana Sheikh', phone: '99887 66554', service: 'Facial', staff: 'Meena Iyer', time: '12:30', status: 'upcoming' },
  { id: 4, client: 'Walk-in Guest', phone: '—', service: 'Manicure', staff: 'Sohail Khan', time: '13:00', status: 'walkin' },
];

function formatDate(date) {
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function Appointments() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('schedule'); // schedule | list
  const [appointments, setAppointments] = useState(initialAppointments);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formType, setFormType] = useState('booking'); // booking | walkin

  const [form, setForm] = useState({
    client: '',
    phone: '',
    service: SERVICES[0],
    staff: STAFF_LIST[0],
    time: TIME_SLOTS[0],
  });

  const changeDay = (delta) => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + delta);
    setCurrentDate(next);
  };

  const openForm = (type) => {
    setFormType(type);
    setForm({
      client: '',
      phone: '',
      service: SERVICES[0],
      staff: STAFF_LIST[0],
      time: TIME_SLOTS[0],
    });
    setShowBookingForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.client.trim()) {
      alert('Please enter client name');
      return;
    }
    const newAppt = {
      id: Date.now(),
      client: form.client,
      phone: form.phone || '—',
      service: form.service,
      staff: form.staff,
      time: form.time,
      status: formType === 'walkin' ? 'walkin' : 'upcoming',
    };
    setAppointments((prev) => [...prev, newAppt]);
    setShowBookingForm(false);
  };

  const updateStatus = (id, status) => {
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, status } : a))
    );
  };

  const sortedAppointments = useMemo(
    () => [...appointments].sort((a, b) => a.time.localeCompare(b.time)),
    [appointments]
  );

  const appointmentsByStaffAndTime = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      map[`${a.staff}-${a.time}`] = a;
    });
    return map;
  }, [appointments]);

  return (
    <div className="appt-page">
      {/* HEADER */}
      <div className="appt-header">
        <div>
          <h2>Appointments</h2>
          <p>Manage bookings, schedules, and walk-ins</p>
        </div>
        <div className="header-actions">
          <button className="btn-outline" onClick={() => openForm('walkin')}>
            <FaWalking /> Add Walk-in
          </button>
          <button className="btn-primary" onClick={() => openForm('booking')}>
            <FaUserPlus /> New Appointment
          </button>
        </div>
      </div>

      {/* DATE NAV + VIEW TOGGLE */}
      <div className="appt-toolbar">
        <div className="date-nav">
          <button className="nav-btn" onClick={() => changeDay(-1)}>
            <FaChevronLeft />
          </button>
          <div className="date-display">
            <FaCalendarAlt className="date-icon" />
            {formatDate(currentDate)}
          </div>
          <button className="nav-btn" onClick={() => changeDay(1)}>
            <FaChevronRight />
          </button>
          <button className="today-btn" onClick={() => setCurrentDate(new Date())}>
            Today
          </button>
        </div>

        <div className="view-toggle">
          <button
            className={view === 'schedule' ? 'active' : ''}
            onClick={() => setView('schedule')}
          >
            <FaThLarge /> Schedule
          </button>
          <button
            className={view === 'list' ? 'active' : ''}
            onClick={() => setView('list')}
          >
            <FaListUl /> List
          </button>
        </div>
      </div>

      {/* SCHEDULE GRID VIEW */}
      {view === 'schedule' && (
        <div className="schedule-wrapper">
          <div
            className="schedule-grid"
            style={{ gridTemplateColumns: `90px repeat(${STAFF_LIST.length}, 1fr)` }}
          >
            <div className="schedule-corner"></div>
            {STAFF_LIST.map((staff) => (
              <div className="schedule-staff-head" key={staff}>
                <FaUserCircle className="staff-avatar-icon" />
                {staff}
              </div>
            ))}

            {TIME_SLOTS.map((slot) => (
              <div className="schedule-row" key={slot} style={{ display: 'contents' }}>
                <div className="schedule-time-label">{slot}</div>
                {STAFF_LIST.map((staff) => {
                  const appt = appointmentsByStaffAndTime[`${staff}-${slot}`];
                  return (
                    <div className="schedule-cell" key={`${staff}-${slot}`}>
                      {appt && (
                        <div className={`appt-block ${STATUS_STYLES[appt.status].cls}`}>
                          <strong>{appt.client}</strong>
                          <span>{appt.service}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <div className="appt-list">
          {sortedAppointments.map((a) => (
            <div className="appt-card" key={a.id}>
              <div className="appt-time-col">
                <FaClock className="time-icon" />
                {a.time}
              </div>

              <div className="appt-info-col">
                <div className="appt-client-row">
                  <FaUserCircle className="client-icon" />
                  <strong>{a.client}</strong>
                  {a.phone !== '—' && (
                    <span className="appt-phone">
                      <FaMobileAlt /> {a.phone}
                    </span>
                  )}
                </div>
                <div className="appt-meta-row">
                  <span>
                    <FaCut /> {a.service}
                  </span>
                  <span>with {a.staff}</span>
                </div>
              </div>

              <div className="appt-status-col">
                <span className={`status-badge ${STATUS_STYLES[a.status].cls}`}>
                  {STATUS_STYLES[a.status].icon}
                  {STATUS_STYLES[a.status].label}
                </span>
                <select
                  value={a.status}
                  onChange={(e) => updateStatus(a.id, e.target.value)}
                  className="status-select"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="walkin">Walk-in</option>
                </select>
              </div>
            </div>
          ))}
          {sortedAppointments.length === 0 && (
            <div className="appt-empty">No appointments for this day</div>
          )}
        </div>
      )}

      {/* BOOKING MODAL */}
      {showBookingForm && (
        <div className="modal-overlay" onClick={() => setShowBookingForm(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {formType === 'walkin' ? 'Add Walk-in Customer' : 'Book New Appointment'}
              </h3>
              <button className="modal-close-btn" onClick={() => setShowBookingForm(false)}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <label className="field-label">Client Name</label>
              <div className="input-group">
                <FaUserCircle className="input-icon" />
                <input
                  type="text"
                  placeholder="Client name"
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  required
                />
              </div>

              <label className="field-label">Phone Number</label>
              <div className="input-group">
                <FaMobileAlt className="input-icon" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              <div className="modal-form-row">
                <div>
                  <label className="field-label">Service</label>
                  <select
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Staff</label>
                  <select
                    value={form.staff}
                    onChange={(e) => setForm({ ...form, staff: e.target.value })}
                  >
                    {STAFF_LIST.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formType === 'booking' && (
                <>
                  <label className="field-label">Time Slot</label>
                  <select
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                  >
                    {TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </>
              )}

              <button type="submit" className="btn-primary full-width">
                {formType === 'walkin' ? 'Add Walk-in' : 'Confirm Booking'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Appointments;